import React, { useEffect, useState, useContext } from "react";

import { makeStyles } from 'tss-react/mui';
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import Campaign from "@mui/icons-material/Campaign";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import Tooltip from "@mui/material/Tooltip";
import Drawer from "@mui/material/Drawer";
import Link from "@mui/material/Link";
import InputLabel from "@mui/material/InputLabel";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Divider from '@mui/material/Divider';
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import EventOutlinedIcon from "@mui/icons-material/EventOutlined";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import useSettings from "../../hooks/useSettings";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import { toast } from "../../services/toast";
import SelectStatusContact from "../SelectStatusContact";
import SelectUser from "../SelectUser";
import AppointmentModal from "../AppointmentModal";
import { AuthContext } from "../../context/Auth/AuthContext";

import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles()(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderLeft: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
	},
	header: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(1, 2),
		minHeight: 56,
	},
	headerTitle: {
		fontWeight: 600,
		fontSize: 15,
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.background.paper,
		flexDirection: "column",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 100,
		height: 100,
	},
	avatarLarge: {
		width: 72,
		height: 72,
		marginBottom: theme.spacing(1),
	},

	contactHeader: {
		display: "flex",
		padding: theme.spacing(2.5, 2, 2),
		flexDirection: "column",
		alignItems: "center",
		textAlign: "center",
	},
	contactName: {
		fontWeight: 600,
		fontSize: 16,
		userSelect: "text",
	},
	copyRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 4,
	},
	copyBtn: {
		padding: 3,
		color: theme.palette.text.secondary,
	},
	contactInfoRow: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		fontSize: 13,
		color: theme.palette.text.secondary,
		marginTop: 4,
		userSelect: "text",
	},
	editButton: {
		marginTop: theme.spacing(1.5),
	},

	whatsappValidateRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexWrap: "wrap",
		gap: 6,
		marginTop: 4,
	},
	whatsappBadgeOk: {
		display: "inline-flex",
		alignItems: "center",
		gap: 3,
		fontSize: 11.5,
		fontWeight: 600,
		color: theme.palette.mode === "light" ? "#1b8a5a" : "#4fd097",
	},
	whatsappBadgeBad: {
		display: "inline-flex",
		alignItems: "center",
		gap: 3,
		fontSize: 11.5,
		fontWeight: 600,
		color: theme.palette.error.main,
	},
	whatsappAlert: {
		margin: theme.spacing(0, 2, 2),
	},

	contactDetails: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
	},
	sectionTitle: {
		fontWeight: 600,
		fontSize: 13,
		marginBottom: theme.spacing(1.5),
	},
	fieldLabel: {
		fontSize: 11.5,
		fontWeight: 600,
		color: theme.palette.text.secondary,
		textTransform: "uppercase",
		letterSpacing: 0.3,
		marginBottom: theme.spacing(0.5),
	},
	fieldGroup: {
		marginTop: theme.spacing(1.5),
		"&:first-of-type": {
			marginTop: 0,
		},
	},
	contactExtraInfo: {
		marginTop: 8,
		padding: theme.spacing(1),
		borderRadius: 8,
		backgroundColor: theme.palette.mode === "light" ? "#f4f6f8" : "#20252c",
	},
	participantsHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1.5),
	},
	campaignBox: {
		display: "flex",
		alignItems: "flex-start",
		gap: 10,
		padding: theme.spacing(1),
		borderRadius: 8,
		backgroundColor: theme.palette.mode === "light" ? "#e7f7f1" : "#14332b",
	},
	campaignName: {
		fontSize: 13.5,
		fontWeight: 600,
		color: theme.palette.text.primary,
		wordBreak: "break-word",
	},
	campaignLink: {
		fontSize: 12,
		color: "#00a884",
	},
	participantsUpdated: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: -theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	participantsList: {
		display: "flex",
		flexDirection: "column",
		gap: 4,
	},
	participantRow: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "6px 4px",
		borderRadius: 8,
		"&:hover": {
			backgroundColor: theme.palette.mode === "light" ? "#f4f6f8" : "#20252c",
		},
		"&:hover $participantCopy": {
			opacity: 1,
		},
	},
	participantAvatar: {
		width: 34,
		height: 34,
		fontSize: 14,
	},
	participantInfo: {
		flex: 1,
		minWidth: 0,
	},
	participantName: {
		fontSize: 13.5,
		fontWeight: 600,
		lineHeight: 1.2,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	participantNumber: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		lineHeight: 1.3,
	},
	participantAdmin: {
		fontSize: 10.5,
		fontWeight: 700,
		color: "#00a884",
		border: "1px solid #00a884",
		borderRadius: 6,
		padding: "1px 6px",
		flex: "0 0 auto",
	},
	participantCopy: {
		opacity: 0,
		transition: "opacity .15s ease",
		flex: "0 0 auto",
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const { classes } = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [representative, setRepresentative] = useState("N/A");
	const [settings, setSettings] = useState({});
	const { getAll: getAllSettings } = useSettings();
	const [apiConnected, setApiConnected] = useState("");
	const [urlExternal, setUrlExternal] = useState(null);
	const [idUser, setIdUser] = useState(null);
	const [companyId, setCompanyId] = useState(null);
	const [idStatus, setIdStatus] = useState(null);
	const [whatsappValidado, setWhatsappValidado] = useState(null);
	const [validandoWhatsapp, setValidandoWhatsapp] = useState(false);
	const { user } = useContext(AuthContext);

	const [groupParticipants, setGroupParticipants] = useState([]);
	const [participantsUpdatedAt, setParticipantsUpdatedAt] = useState(null);
	const [loadingParticipants, setLoadingParticipants] = useState(false);

	const isAdmin = user.profile === "admin" || user.profile === "supervisor";
	const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
	const [appointmentUsers, setAppointmentUsers] = useState([]);

	const handleOpenAppointmentModal = async () => {
		if (isAdmin && appointmentUsers.length === 0) {
			try {
				const { data } = await api.get("/users/list");
				setAppointmentUsers(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		}
		setAppointmentModalOpen(true);
	};

	const fetchGroupParticipants = async (refresh = false) => {
		if (!ticket?.isGroup || !ticket?.id) return;
		setLoadingParticipants(true);
		try {
			const { data } = await api.get(
				`/tickets/${ticket.id}/group-participants`,
				refresh ? { params: { refresh: true } } : undefined
			);
			setGroupParticipants(data?.participants || []);
			setParticipantsUpdatedAt(data?.updatedAt || null);
		} catch (err) {
			setGroupParticipants([]);
			setParticipantsUpdatedAt(null);
		} finally {
			setLoadingParticipants(false);
		}
	};

	useEffect(() => {
		if (!open || !ticket?.isGroup || !ticket?.id) {
			setGroupParticipants([]);
			return;
		}
		fetchGroupParticipants(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, ticket?.id, ticket?.isGroup]);

	useEffect(() => {
		async function findData() {
			const settingList = await getAllSettings();
			setSettings(settingList);
			if (Array.isArray(settingList)) {
				const apiConnected = settingList.find(
					(d) => d.key === "apiConnected"
				);

				if (apiConnected && apiConnected.value !== '') {
					setApiConnected(apiConnected.value);
				}
			}
		}
		findData();

		setCompanyId(user.companyId);
		setIdUser(user.id);
	}, []);

	useEffect(() => {
		setIdStatus(contact.statusId ?? null);
		setWhatsappValidado(contact.whatsappValidated ?? null);
		if (contact.URLExternal) {
			setUrlExternal(contact?.URLExternal ?? null);
		} else {
			setUrlExternal(null);
		}

	}, [contact]);

	const handleCRM = async () => {
		const extraInfo =
			contact?.extraInfo && contact.extraInfo.length > 0
				? contact.extraInfo
					.map(info => `${info.name}: ${info.value}`)
					.join(" | ")
				: "";

		const params = {
			contactId: contact?.id,
			name: contact?.name ?? "",
			phonePrimary: contact?.number ?? null,
			emailPrimary: contact?.email ?? null,
			idStatus: contact?.status?.name ?? null,
			photo: contact?.profilePicUrl ?? null,
			info: (extraInfo ? extraInfo + " | " : "") + "Migrado desde ChatEcarga.",
			user: {
				name: user?.name ?? "",
				email: user?.email ?? ""
			}
		};

		try {
			const response = await api.post("/contact/synchronize", params);
			setUrlExternal(response.data.url);
			toast.success(response?.message);

		} catch (error) {
			toastError(error);
		}

	};

	const fetchUsers = async () => {
		const { data } = await api.get("/users/" + contact.iduser);
		setRepresentative(data.name);
	};

	if (contact.iduser != undefined || contact.iduser != null) {
		fetchUsers();
	}

	const guardarEstadoContacto = async (nuevoIdStatus, nuevoIdUser) => {
		try {
			const { data } = await api.post("/contact/status/update", {
				idStatus: nuevoIdStatus,
				contactId: contact.id,
				idUser: nuevoIdUser,
				idTicket: ticket?.id ?? null
			});
			setIdStatus(data.statusId);
			toast.success("Estado del contacto actualizado.");

			if (nuevoIdUser !== user.id) {
				window.location.reload();
			}
		} catch (error) {
			toastError(error);
		}
	};

	const updateContactStatus = (value) => {
		guardarEstadoContacto(value, idUser);
	};

	const updateContactRepresentative = (value) => {
		setIdUser(value);
		guardarEstadoContacto(idStatus, value);
	};

	const validarWhatsapp = async () => {
		setValidandoWhatsapp(true);
		try {
			const { data } = await api.post(
				`/contacts/${contact.id}/validate-whatsapp`,
				{ whatsappId: ticket?.whatsappId ?? undefined }
			);
			setWhatsappValidado(data.whatsappValidated);
			if (data.exists) {
				toast.success("El número tiene WhatsApp.");
			} else {
				toast.error("El número no está registrado en WhatsApp.");
			}
		} catch (error) {
			toastError(error);
		} finally {
			setValidandoWhatsapp(false);
		}
	};

	const copyToClipboard = async (text, label) => {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(String(text));
			toast.success(`${label} copiado`);
		} catch (err) {
			toast.error("No se pudo copiar");
		}
	};

	return (
        <>
            <Drawer
                className={classes.drawer}
                variant="persistent"
                anchor="right"
                open={open}
                ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
                classes={{
					paper: classes.drawerPaper,
				}}
                slotProps={{
                    backdrop: { style: { position: "absolute" } },
                    paper: { style: { position: "absolute" } }
                }}>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose} size="large">
						<CloseIcon />
					</IconButton>
					<Typography className={classes.headerTitle}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<div className={classes.contactHeader}>
							<Avatar
								src={contact.profilePicUrl}
								alt="contact_image"
								className={classes.avatarLarge}
							/>
							<div className={classes.copyRow}>
								<Typography className={classes.contactName}>
									{contact.name}
								</Typography>
								<Tooltip title="Copiar nombre">
									<IconButton
										className={classes.copyBtn}
										size="small"
										onClick={() => copyToClipboard(contact.name, "Nombre")}
									>
										<ContentCopyIcon style={{ fontSize: 15 }} />
									</IconButton>
								</Tooltip>
							</div>
							{contact.number && (
								<Typography component="div" className={classes.contactInfoRow}>
									<Link href={`tel:${contact.number}`} underline="hover" color="inherit">
										{contact.number}
									</Link>
									<Tooltip title="Copiar número">
										<IconButton
											className={classes.copyBtn}
											size="small"
											onClick={() => copyToClipboard(contact.number, "Número")}
										>
											<ContentCopyIcon style={{ fontSize: 15 }} />
										</IconButton>
									</Tooltip>
								</Typography>
							)}
							{}
							{contact.number && ticket?.channel === "whatsapp" && !ticket?.isGroup && (
								<div className={classes.whatsappValidateRow}>
									{whatsappValidado === true && (
										<span className={classes.whatsappBadgeOk}>
											<CheckCircleOutlinedIcon style={{ fontSize: 14 }} />
											Verificado
										</span>
									)}
									{whatsappValidado === false && (
										<span className={classes.whatsappBadgeBad}>
											<ErrorOutlineIcon style={{ fontSize: 14 }} />
											No tiene WhatsApp
										</span>
									)}
									<Button
										size="small"
										variant="text"
										disabled={validandoWhatsapp}
										startIcon={
											validandoWhatsapp
												? <CircularProgress size={12} />
												: <WhatsAppIcon style={{ fontSize: 16 }} />
										}
										onClick={validarWhatsapp}
									>
										{whatsappValidado !== null
											? "Revalidar"
											: "Validar WhatsApp"}
									</Button>
								</div>
							)}
							{}
							{contact.username && (
								<Typography component="div" className={classes.contactInfoRow}>
									@{contact.username}
									<Tooltip title="Copiar usuario">
										<IconButton
											className={classes.copyBtn}
											size="small"
											onClick={() => copyToClipboard(contact.username, "Usuario")}
										>
											<ContentCopyIcon style={{ fontSize: 15 }} />
										</IconButton>
									</Tooltip>
								</Typography>
							)}
							{contact.email && (
								<Typography component="div" className={classes.contactInfoRow}>
									<Link href={`mailto:${contact.email}`} underline="hover" color="inherit">
										{contact.email}
									</Link>
								</Typography>
							)}
							{representative && representative !== "N/A" && (
								<Typography component="div" className={classes.contactInfoRow}>
									R: {representative}
								</Typography>
							)}
							<Button
								variant="outlined"
								color="primary"
								size="small"
								className={classes.editButton}
								onClick={() => setModalOpen(true)}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
						</div>
						{whatsappValidado === false && ticket?.channel === "whatsapp" && !ticket?.isGroup && (
							<Alert severity="warning" className={classes.whatsappAlert}>
								Este número no parece tener WhatsApp. Confirmá el dato con el cliente antes de seguir.
							</Alert>
						)}
						<Divider />
						{ticket?.adReferralTitle && (
							<>
								<div className={classes.contactDetails}>
									<Typography variant="subtitle1" className={classes.sectionTitle}>
										Anuncio de origen
									</Typography>
									<div className={classes.campaignBox}>
										<Campaign fontSize="small" style={{ color: "#00a884" }} />
										<div>
											<div className={classes.campaignName}>
												{ticket.adReferralCampaign || ticket.adReferralTitle}
											</div>
											{(() => {
												try {
													const d = JSON.parse(ticket.adReferralData || "{}");
													return d.source_url ? (
														<Link
															href={d.source_url}
															target="_blank"
															rel="noopener noreferrer"
															underline="hover"
															className={classes.campaignLink}
														>
															Ver anuncio
														</Link>
													) : null;
												} catch (e) {
													return null;
												}
											})()}
										</div>
									</div>
								</div>
								<Divider />
							</>
						)}
						{ticket?.isGroup && (
							<>
								<div className={classes.contactDetails}>
									<div className={classes.participantsHeader}>
										<Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
											Participantes{groupParticipants.length > 0 ? ` (${groupParticipants.length})` : ""}
										</Typography>
										<Tooltip title="Refrescar participantes">
											<span>
												<IconButton
													size="small"
													disabled={loadingParticipants}
													onClick={() => fetchGroupParticipants(true)}
												>
													<RefreshIcon style={{ fontSize: 18 }} />
												</IconButton>
											</span>
										</Tooltip>
									</div>
									{participantsUpdatedAt && !loadingParticipants && (
										<Typography className={classes.participantsUpdated}>
											Actualizado: {new Date(participantsUpdatedAt).toLocaleString()}
										</Typography>
									)}
									{loadingParticipants ? (
										<div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
											<CircularProgress size={22} />
										</div>
									) : groupParticipants.length === 0 ? (
										<Typography className={classes.participantNumber}>
											No se pudieron cargar los participantes.
										</Typography>
									) : (
										<div className={classes.participantsList}>
											{groupParticipants.map((p) => (
												<div key={p.id} className={classes.participantRow}>
													<Avatar className={classes.participantAvatar}>
														{(p.name || p.number || "?").charAt(0).toUpperCase()}
													</Avatar>
													<div className={classes.participantInfo}>
														<div className={classes.participantName}>
															{p.name || p.number}
														</div>
														{p.name && (
															<div className={classes.participantNumber}>{p.number}</div>
														)}
													</div>
													{p.isAdmin && (
														<span className={classes.participantAdmin}>admin</span>
													)}
													<Tooltip title="Copiar número">
														<IconButton
															className={classes.participantCopy}
															size="small"
															onClick={() => copyToClipboard(p.number, "Número")}
														>
															<ContentCopyIcon style={{ fontSize: 15 }} />
														</IconButton>
													</Tooltip>
												</div>
											))}
										</div>
									)}
								</div>
								<Divider />
							</>
						)}
						<div className={classes.contactDetails}>
							<Typography variant="subtitle1" className={classes.sectionTitle}>
								Status del Contacto
							</Typography>
							<div className={classes.fieldGroup}>
								<Typography className={classes.fieldLabel}>Estado</Typography>
								<SelectStatusContact selectId={idStatus} onChange={updateContactStatus} />
							</div>

							<div className={classes.fieldGroup}>
								<Typography className={classes.fieldLabel}>Representante</Typography>
								<SelectUser
									selectUserid={idUser}
									companyId={companyId}
									onChange={updateContactRepresentative}
								/>
							</div>
							<div className={classes.fieldGroup}>
								<Button
									variant="outlined"
									color="primary"
									size="small"
									fullWidth
									startIcon={<EventOutlinedIcon fontSize="small" />}
									onClick={handleOpenAppointmentModal}
								>
									Agendar cita
								</Button>
							</div>
						</div>

						<Divider />
						<div className={classes.contactDetails}>
							<Typography variant="subtitle1" className={classes.sectionTitle}>
								{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
							</Typography>
							<ContactNotes ticket={ticket} />
						</div>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<AppointmentModal
							open={appointmentModalOpen}
							onClose={() => setAppointmentModalOpen(false)}
							appointment={null}
							initialContact={contact}
							users={appointmentUsers}
							currentUser={user}
							canAssignOthers={isAdmin}
							onSaved={() => {}}
						/>
						{(contact?.extraInfo?.length > 0 || apiConnected) && (
							<>
								<Divider />
								<div className={classes.contactDetails}>
									<Typography variant="subtitle1" className={classes.sectionTitle}>
										{i18n.t("contactDrawer.extraInfo")}
									</Typography>
									{contact?.extraInfo?.map(info => (
										<Paper
											key={info.id}
											elevation={0}
											className={classes.contactExtraInfo}
										>
											<InputLabel>{info.name}</InputLabel>
											<Typography component="div" noWrap style={{ paddingTop: 2 }}>
												<MarkdownWrapper>{info.value}</MarkdownWrapper>
											</Typography>
										</Paper>
									))}
									{apiConnected && (
										<div>
											<Divider style={{ marginTop: '10px', marginBottom: '10px' }} />
											<Typography variant="subtitle1" className={classes.sectionTitle}>
												{i18n.t("contactDrawer.connectedInfo")}
											</Typography>

											{urlExternal ? (
												<Button
													variant="contained"
													color="primary"
													size="small"
													style={{ marginRight: 10 }}
													href={urlExternal}
													target="_blank"
													rel="noopener noreferrer"
												>
													{i18n.t("contactDrawer.viewExternalProfile") || "Ver perfil"}
												</Button>
											) : (
												<Button variant="outlined" size="small" style={{ marginRight: 10 }} disabled>
													{i18n.t("contactDrawer.viewExternalProfile") || "Ver perfil"}
												</Button>
											)}

											<Button
												variant="outlined"
												color="primary"
												style={{ fontSize: 12 }}
												onClick={() => handleCRM()}
											>
												{i18n.t("contactDrawer.buttons.sendToCRM")}
											</Button>
										</div>
									)}
								</div>
							</>
						)}

					</div>
				)}
			</Drawer>
        </>
    );
};

export default ContactDrawer;
